import moment from 'moment-timezone'
import * as ACT from 'actions/actionTypes'
import { getCurrency, getTimezone, getDateFormat, getTenant } from 'utils/authService'
import Cookies from 'universal-cookie'
import { COOKIES } from 'utils/constants'
import cloneDeep from 'lodash/cloneDeep'
import shuffle from 'lodash/shuffle'
import { toDealStatuses } from 'utils/serializers'
import differenceBy from 'lodash/differenceBy'
import { getModules } from 'utils/paymentService'

const formatNumber = number => {
  return Math.floor(number)
    .toString()
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
}

export const currencyFormatter = params => {
  return params && (params.value || params.value === 0)
    ? getCurrency().symbol + formatNumber(params.value)
    : ''
}

export const percentageFormatter = params => {
  return params && (params.value || params.value === 0) ? formatNumber(params.value) + '%' : ''
}

export const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1)

export const groupBy = (items, key) =>
  items.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item]
    }),
    {}
  )

export const preventDefault = event => {
  event.stopPropagation()
  event.preventDefault()
}

export const whenIsDate = date => {
  // returns 0 if today, negative if in the future else positive
  const today = moment(new Date())
  const dueDate = moment(date)
  return !dueDate.isSame(today, 'day') && today.diff(dueDate.format('MM/DD/YYYY'))
}

export const refreshNotifications = put => {
  return put({
    type: ACT.NOTIFICATIONS_FETCH_REQUESTED
  })
}

export const refreshUserProfile = put => {
  return put({
    type: ACT.GET_LOGGED_IN_USER_FETCH_REQUESTED
  })
}

export const hasJsonStructure = str => {
  if (typeof str !== 'string') return false
  try {
    const result = JSON.parse(str)
    const type = Object.prototype.toString.call(result)
    return type === '[object Object]' || type === '[object Array]'
  } catch (err) {
    return false
  }
}

export const convertImgToBase64URL = (url, callback) => {
  const img = new Image()
  img.crossOrigin = 'Anonymous'
  img.onload = () => {
    let canvas = document.createElement('CANVAS')
    const ctx = canvas.getContext('2d')
    canvas.height = img.height
    canvas.width = img.width
    ctx.drawImage(img, 0, 0)
    const dataURL = canvas.toDataURL()
    callback(dataURL)
    canvas = null
  }
  img.src = url
}

export const convertImgToBlob = (url, callback) => {
  const img = new Image()
  img.crossOrigin = 'Anonymous'
  img.onload = () => {
    let canvas = document.createElement('CANVAS')
    const ctx = canvas.getContext('2d')
    canvas.height = img.height
    canvas.width = img.width
    ctx.drawImage(img, 0, 0)
    canvas.toBlob(
      function (blob) {
        callback(blob)
      },
      'image/jpeg',
      1
    )
    canvas = null
  }
  img.src = url
}

export const getPriorityColor = status => {
  let color
  if (!status) {
    return ''
  }
  switch (status.toLowerCase()) {
    case 'hot':
      color = 'red'
      break
    case 'warm':
      color = 'orange'
      break
    case 'cold':
      color = 'turquoise'
      break
    case 'follow-up':
      color = 'green'
      break
    default:
      color = 'grey'
  }
  return color
}

export const numberRange = (start, end, oddOrEven) => {
  if (!oddOrEven) return new Array(end + 1 - start).fill().map((d, i) => i + start)
  if (oddOrEven === 'even') {
    return new Array(end + 1 - start)
      .fill()
      .map((d, i) => i + start)
      .filter(d => d % 2 === 0)
  } else {
    return new Array(end + 1 - start)
      .fill()
      .map((d, i) => i + start)
      .filter(d => d % 2 !== 0)
  }
}

export const categoryMapper = {
  late: 'lateTasks',
  today: 'todayTasks',
  scheduled: 'futureTasks',
  completed: 'completedTasks'
}

export const getFirstTaskDueDate = (parent, currentTask) => {
  let uncompletedTasksDates = []
  for (const key in categoryMapper) {
    uncompletedTasksDates =
      key !== 'completed'
        ? uncompletedTasksDates.concat(
            parent[categoryMapper[key]].filter(t => t.id !== currentTask.id).map(t => t.dueDate)
          )
        : uncompletedTasksDates
  }
  return uncompletedTasksDates?.sort((d1, d2) => new Date(d1.date) - new Date(d2.date))[0]
}

export const stripHtml = html => {
  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

export const getNoBidClientStyle = noBid => ({ color: noBid ? '#fa2735' : '' })

export const utcDate = (date, format, timezone) => {
  if (!format)
    moment
      .tz(moment(date).format('YYYY-MM-DD HH:mm:ss'), timezone || getTimezone())
      .utc()
      .format()
  return moment
    .tz(moment(date).format('YYYY-MM-DD HH:mm:ss'), timezone || getTimezone())
    .utc()
    .format(format)
}

export const utcCurrentDate = (format, isDateObj = false) => {
  if (isDateObj) {
    return moment.tz(moment().format('YYYY-MM-DD HH:mm:ss'), getTimezone()).utc()
  }
  if (!format) moment.tz(moment().format('YYYY-MM-DD HH:mm:ss'), getTimezone()).utc().format()
  return moment.tz(moment().format('YYYY-MM-DD HH:mm:ss'), getTimezone()).utc().format(format)
}

export const timezoneDate = (
  date,
  timeFormat,
  splitCharacter = ' ',
  noFormat = false,
  customFormat,
  timezone
) => {
  let format = customFormat || getDateFormat()
  if (timeFormat) {
    format = `${format}${splitCharacter}${timeFormat}`
  }
  if (noFormat) return moment.tz(date, timezone || getTimezone())
  return moment.tz(date, timezone || getTimezone()).format(format)
}

export const removeSubstringAfterCharacter = (string, char) =>
  string.substr(0, string.lastIndexOf(char))

export const sortAlphabetically = (arr, key) => {
  return arr.sort(function (a, b) {
    if (a[key].toLowerCase() < b[key].toLowerCase()) return -1
    if (a[key].toLowerCase() > b[key].toLowerCase()) return 1
    return 0
  })
}

export const fromReactComponentToHtmlDocument = (html, title = 'Proposal') => {
  const newDoc = document.implementation.createHTMLDocument(title)
  newDoc.body.innerHTML = html
  newDoc.body.style = 'margin: 0'
  newDoc.querySelectorAll('.remove-for-pdf-js').forEach(e => e.remove())
  return newDoc.documentElement.outerHTML
}

export const getCorrectDroppedOffsetValue = (ref, initialPosition, finalPosition) => {
  // get the container (view port) position by react ref...
  const dropTargetPosition = ref.getBoundingClientRect()

  const { y: finalY, x: finalX } = finalPosition
  const { y: initialY, x: initialX } = initialPosition

  // calculate the correct position removing the viewport position.
  // finalY > initialY, I'm dragging down, otherwise, dragging up
  const newYposition =
    finalY > initialY
      ? initialY + (finalY - initialY) - dropTargetPosition.top
      : initialY - (initialY - finalY) - dropTargetPosition.top

  const newXposition =
    finalX > initialX
      ? initialX + (finalX - initialX) - dropTargetPosition.left
      : initialX - (initialX - finalX) - dropTargetPosition.left

  return {
    x: newXposition,
    y: newYposition
  }
}

export const clearCookies = () => {
  const cookies = new Cookies()
  for (const COOKIE in COOKIES) {
    cookies.remove(COOKIES[COOKIE])
  }
}

export const getHelpUserEmail = () => `alex+${getTenant()}@idealcrm.app`

export const serializeNullToEmptyString = arr => {
  const cloneArr = cloneDeep(arr)
  cloneArr.forEach(el => {
    Object.keys(el).forEach(key => {
      if (el[key] === null) {
        el[key] = ''
      }
    })
  })
  return cloneArr
}

export const sortClientsByAwarded = (clientsList, awardedClient) => {
  if (awardedClient) {
    return clientsList?.length
      ? [
          ...clientsList.filter(c =>
            c.value ? +c.value === +awardedClient?.value : +c.id === +awardedClient?.value
          ),
          ...clientsList.filter(c =>
            c.value ? +c.value !== +awardedClient?.value : +c.id !== +awardedClient?.value
          )
        ]
      : []
  }

  return clientsList
}

export const sortDealCompaniesByAwarded = (dealCompanies, awardedCompany) => {
  if (awardedCompany) {
    return dealCompanies?.length
      ? [
          ...dealCompanies.filter(deal => deal.company?.id === awardedCompany?.id),
          ...dealCompanies.filter(deal => deal.company?.id !== awardedCompany?.id)
        ]
      : []
  }

  return dealCompanies
}

export const formatProposalCardDealStatus = (statuses, code, showInfoText = true) => {
  const foundStatus = statuses?.find(status => status.code === code) || null
  return foundStatus ? `${showInfoText ? 'Opportunity status = ' : ''}${foundStatus.name}` : ''
}

export const generatePassword = passwordLength => {
  const numberChars = '0123456789'
  const upperChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowerChars = 'abcdefghijklmnopqrstuvwxyz'
  const allChars = numberChars + upperChars + lowerChars
  let randPasswordArray = Array(passwordLength)
  randPasswordArray[0] = numberChars
  randPasswordArray[1] = upperChars
  randPasswordArray[2] = lowerChars
  randPasswordArray = randPasswordArray.fill(allChars, 3)
  return shuffle(randPasswordArray.map(x => x[Math.floor(Math.random() * x.length)])).join('')
}
export const getOrderedStatuses = (userStatuses = [], organizationStatuses) => {
  const userStatusesGroup = userStatuses
    .filter(s => s.visibleOnBoardLanes)
    .sort((a, b) => a.orderIndex - b.orderIndex)

  const organizationStatusesGroup = differenceBy(organizationStatuses, userStatusesGroup, 'id')

  const options = [
    {
      label: 'Personal Pipeline',
      options: toDealStatuses(userStatusesGroup)
    },
    {
      label: 'Organization Pipeline',
      options: toDealStatuses(organizationStatusesGroup)
    }
  ]

  return options
}

export const hasModule = module => {
  const modules = getModules('MODULES')
  return Object.keys(modules).includes(module) && modules[module]
}
